import './DashboardHeader.scss';
import { Form } from 'react-bootstrap';
import { MdArrowBack, MdEdit } from 'react-icons/md';
// Undo/Redo icons are only used by the hidden undo/redo buttons below — restore with them.
// import { MdUndo, MdRedo } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { DatePicker } from '@Components/DatePicker';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { ResolvedRoleLevels } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { CustomPresetState } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { useIsMutating } from '@tanstack/react-query';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Appearance, Design, Size } from '@Components/designConstants';
import { useAppSelector } from '@src/redux';
import { roleSelector } from '@src/redux/user';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { PermissionDropdown } from '../Charts/PermissionDropdown';
import { ViewOnlyBadge } from '../Charts/ViewOnlyBadge';
import { useSharingRequest } from '../Charts/hooks/useSharingRequest';
import { sharingTargetKey } from '../Charts/hooks/usePendingSharing';
import { SharingTargetState } from '../Charts/sharing-request-utils';
import { useDashboardView } from './DashboardViewContext';
import { withComparisonSelection, withPrimarySelection } from './date-config';
import { toChartDateRange, toPickerRange } from './date-range';
import { dashboardMutationKey, UNSAVED_ID } from './hooks/useDashboard';
import { TemplateAgentTip } from './TemplateAgentTip';

interface DashboardHeaderProps {
  isEditMode: boolean;
  // Whether the buffered layout differs from what's stored. Only consulted in VIEW mode: resizing a
  // card is reachable there and now buffers instead of writing straight through, so without this the
  // change would be invisible AND uncommittable until the user happened to enter edit mode.
  hasPendingChanges: boolean;
  // Whether this user may change the dashboard. Edit mode turns on drag, per-card removal and
  // rename together, so when the user cannot edit there is nothing useful behind the button —
  // hide it rather than letting them into a mode where every control refuses them.
  canEdit: boolean;
  // The dashboard's stored role levels. BOTH optional, and the absence is meaningful: an UNSAVED
  // dashboard has no row and therefore no levels, and there is nothing to share until there is a row
  // to govern — so the slot below draws neither the control nor the badge.
  viewRoleRequired?: number;
  editRoleRequired?: number;
  onPermissionChange: (levels: ResolvedRoleLevels) => Promise<void>;
  // This dashboard's id, for the chat-driven sharing note's target. `UNSAVED_ID` here means the
  // dashboard has never been saved, which the sharing decision refuses rather than writes.
  dashboardId: string;
  // The buffered dashboard name, editable only in edit mode; persisted alongside layout on Save.
  nameDraft: string;
  onNameChange: (name: string) => void;
  // True when any chart on this dashboard is a period-over-period comparison chart — gates the global
  // comparison date range.
  showComparison: boolean;
  onEnterEdit: () => void;
  onSaveChanges: () => void;
  onDiscardChanges: () => void;
  // Leaves this dashboard for the dashboards list — there's no in-place switcher, so this is a
  // plain back navigation rather than anything dashboard-specific.
  onBack: () => void;
  // Reserved — still wired from DashboardBuilder but their buttons are hidden until the features are
  // real. The only manual dashboard edits today are drag-and-drop and rename. Restore the buttons +
  // these props' use together.
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onAddSection: () => void;
  onAddCards: () => void;
}

export const DashboardHeader = ({
  isEditMode,
  hasPendingChanges,
  canEdit,
  viewRoleRequired,
  editRoleRequired,
  onPermissionChange,
  dashboardId,
  nameDraft,
  onNameChange,
  showComparison,
  onEnterEdit,
  onSaveChanges,
  onDiscardChanges,
  onBack
}: DashboardHeaderProps) => {
  const {
    globalRange,
    setGlobalRange,
    globalComparisonRange,
    setGlobalComparisonRange,
    globalDateConfig,
    setGlobalDateConfig
  } = useDashboardView();

  const { cid } = useCompanyInfo();
  // The role straight off the selector, not via `useRecordPermissions`: this component already has
  // `canEdit` as a prop, so that hook would compute a second answer we discard — and it would need a
  // record object built purely to satisfy an argument it does not use. `PermissionDropdown` below
  // reads the same value the same way, so the two cannot drift.
  const userRole = useAppSelector(roleSelector);

  // The record's pair, or `undefined` when there is no row to govern. Derived ONCE and handed down,
  // because three things need it: the sharing target below, `DashboardSharing`'s own early return,
  // and the dropdown it renders. Re-deriving it in the sub-component is the same both-or-undefined
  // narrowing written twice, with nothing keeping the two spellings in step.
  const levels =
    viewRoleRequired === undefined || editRoleRequired === undefined
      ? undefined
      : { viewRoleRequired, editRoleRequired };

  // Is a dashboard write already in flight? `isEditMode` alone is not enough: `handleSaveChanges`
  // fires the layout mutation fire-and-forget and flips the mode in the same tick, so the note would
  // be consumed while that PUT is still out — and `handlePermissionChange` would then send the
  // PRE-EDIT config, because the query is only re-seeded in the mutation's `onSuccess`. Two
  // concurrent writes, and the loser reverts the other. Removing a card in VIEW mode persists the
  // same way, with `isEditMode` already false, so a mode flag never covered that path at all.
  const isSavingDashboard =
    useIsMutating({ mutationKey: dashboardMutationKey(cid, dashboardId) }) > 0;

  // MUST live here, not inside `DashboardSharing` below — that component mounts ONLY in the
  // `!isEditMode` branch, so a hook placed there is unmounted the moment the user clicks "Edit
  // Dashboard". A chat-driven sharing request that arrives while editing would then have no consumer
  // at all: the agent reports success, the note sits in the cache with `gcTime: Infinity`, and it
  // applies itself — silently, possibly much later — the next time this dashboard is reopened in view
  // mode. Hoisting the hook here, above the `isEditMode` split, runs it in BOTH modes, matching how
  // the chart surface's own `useSharingRequest` sits above every early return in SingleChartView.
  //
  // A dashboard at UNSAVED_ID has no row. `useCanEditDashboard` answers TRUE for it — deliberately,
  // so the first save at a company with no dashboards is not blocked — so without this arm a sharing
  // request would fall past the permission check and CREATE the row as a side effect.
  const { openWith, closeConfirm } = useSharingRequest({
    company: cid,
    noteTarget: sharingTargetKey(dashboardId),
    target:
      dashboardId === UNSAVED_ID || levels === undefined
        ? { state: SharingTargetState.UNSAVED }
        : { state: SharingTargetState.GOVERNED, levels },
    userRole,
    canEdit,
    page: PAGE_NAMES.DASHBOARD,
    // Held, not dropped, for two separate reasons. (1) `isEditMode`: while the builder has
    // uncommitted layout edits, a write here replaces the dashboard query's data, which re-seeds
    // DashboardBuilder's history and discards whatever the user was mid-way through arranging.
    // (2) `isSavingDashboard`: while ANY dashboard write is actually in flight — a layout save whose
    // fire-and-forget `mutate` hasn't resolved yet, or a card removed straight from view mode — so
    // this write doesn't race it with a stale pre-save `config` (see `useIsMutating` above). `defer`
    // holds the request without consuming it, so it runs the moment both conditions clear.
    defer: isEditMode || isSavingDashboard,
    onSave: onPermissionChange
  });

  // Two things move together on every pick: the concrete window the charts render, and the picker
  // SELECTION behind it (a preset label, a custom-preset state, or neither). The selection is what
  // a dashboard saves — storing "MTD" rather than the dates MTD happens to mean today is the whole
  // point — so it's recorded here even in view mode, where it stays transient until someone saves
  // the dashboard in edit mode.
  const commonPickerProps = {
    range: toPickerRange(globalRange),
    dateConfig: globalDateConfig,
    onChange: (
      r: DateRange,
      selection: {
        selectedPreset: string | null;
        startCustomPreset: CustomPresetState;
        endCustomPreset: CustomPresetState;
      }
    ) => {
      setGlobalRange(toChartDateRange(r));
      setGlobalDateConfig(previous => withPrimarySelection(previous, r, selection));
    },
    datePresets: true,
    design: Design.BRAND,
    size: Size.MD
  };

  return (
    <div className='dashboard-header'>
      {isEditMode ? (
        // Rename is buffered like the layout — it edits the draft here and persists only on Save.
        // Sized to match the dashboard's own title (heading2Bold, same as a chart's title) so it reads
        // as the name, not a form field — the leading pencil is what marks it editable instead.
        <div className='dashboard-header-name-edit'>
          <MdEdit className='dashboard-header-name-edit-icon' />
          <Form.Control
            className='dashboard-header-name-input'
            value={nameDraft}
            placeholder='Dashboard name'
            onChange={e => onNameChange(e.target.value)}
          />
        </div>
      ) : (
        // The dashboard's own name is already the page title (see DashboardView's <Page title>) —
        // repeating it here would be redundant, so this is just a way back to the list.
        <Button
          size={Size.LG}
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          leadingElement={<MdArrowBack />}
          onClick={onBack}
        >
          Back to all dashboards
        </Button>
      )}
      <div className='dashboard-header-actions'>
        {isEditMode ? (
          <>
            {/* Hidden until wired up — no working functionality behind them yet. The only manual
                dashboard edits today are drag-and-drop and rename, so those are all the user should
                think they can do. Restore alongside the reserved props above when implemented.
            <IconButton onClick={onUndo} disabled={!canUndo} tooltip={{ text: 'Undo' }}>
              <MdUndo />
            </IconButton>
            <IconButton onClick={onRedo} disabled={!canRedo} tooltip={{ text: 'Redo' }}>
              <MdRedo />
            </IconButton>
            <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onAddSection}>
              Add Section
            </Button>
            <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onAddCards}>
              Add Card
            </Button>
            */}
            <Button
              size={Size.LG}
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              disabled={isSavingDashboard}
              onClick={onDiscardChanges}
            >
              Discard changes
            </Button>
            <Button size={Size.LG} disabled={isSavingDashboard} onClick={onSaveChanges}>
              Save changes
            </Button>
          </>
        ) : (
          <>
            {/* View mode draws TWO things: who the dashboard is shared with, and the way into edit
                mode. Edit mode already fills this space with Discard/Save, and its own controls are
                only reachable once `canEdit` let the user in. A dashboard with no levels has no row
                yet (see the props above), so neither arm draws. */}
            <DashboardSharing
              canEdit={canEdit}
              levels={levels}
              onPermissionChange={onPermissionChange}
              openWith={openWith}
              onExternalClose={closeConfirm}
            />
            {/* A pending resize made from view mode. Same two actions edit mode offers, so there is
                one way to commit a layout change and one way to abandon it rather than a second
                vocabulary for the same thing. Edit layout stays available beside them: a pending
                change carries into edit mode untouched, so entering it is a continuation. */}
            {canEdit && hasPendingChanges && (
              <>
                <Button
                  size={Size.LG}
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  disabled={isSavingDashboard}
                  onClick={onDiscardChanges}
                >
                  Discard changes
                </Button>
                <Button size={Size.LG} disabled={isSavingDashboard} onClick={onSaveChanges}>
                  Save changes
                </Button>
              </>
            )}
            <TemplateAgentTip dashboardId={dashboardId} />
            {canEdit && (
              <Button
                appearance={Appearance.OUTLINED}
                design={Design.NEUTRAL}
                leadingElement={<MdEdit />}
                onClick={onEnterEdit}
              >
                Edit layout
              </Button>
            )}
          </>
        )}
        {showComparison ? (
          <DatePicker
            {...commonPickerProps}
            comparison
            comparisonRange={toPickerRange(globalComparisonRange)}
            comparisonOnChange={(
              r: DateRange,
              selection: {
                selectedCompPerType: string | null;
                startCompCustomPreset: CustomPresetState;
                endCompCustomPreset: CustomPresetState;
              }
            ) => {
              setGlobalComparisonRange(toChartDateRange(r));
              setGlobalDateConfig(previous => withComparisonSelection(previous, r, selection));
            }}
          />
        ) : (
          <DatePicker {...commonPickerProps} />
        )}
      </div>
    </div>
  );
};

interface DashboardSharingProps {
  canEdit: boolean;
  // The dashboard's stored pair, already narrowed by the parent — `undefined` means there is no
  // saved row yet, and therefore nothing to show and nothing to govern.
  levels?: ResolvedRoleLevels;
  onPermissionChange: (levels: ResolvedRoleLevels) => Promise<void>;
  // Both owned by `DashboardHeader` now — see its own note on why `useSharingRequest` cannot live in
  // this sub-component. Handed down exactly as `PermissionDropdown` wants them.
  openWith: ResolvedRoleLevels | undefined;
  onExternalClose: () => void;
}

// The sharing slot: the control for somebody who may change this dashboard, the badge for somebody
// who may only read it, and nothing at all until there is a saved row carrying levels to show.
const DashboardSharing = ({
  canEdit,
  levels,
  onPermissionChange,
  openWith,
  onExternalClose
}: DashboardSharingProps) => {
  if (levels === undefined) return undefined;
  if (!canEdit) return <ViewOnlyBadge />;

  return (
    <PermissionDropdown
      // The two plain numbers `PermissionDropdown`'s interface declares, not the object — unpacked
      // here, after the check above has narrowed `levels` away from `undefined`.
      viewRoleRequired={levels.viewRoleRequired}
      editRoleRequired={levels.editRoleRequired}
      page={PAGE_NAMES.DASHBOARD}
      openWith={openWith}
      onExternalClose={onExternalClose}
      onSave={onPermissionChange}
    />
  );
};
